import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Award, Send, Download, Search, AlertCircle, CheckCircle, X, Users, Calendar, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

export default function AdminCertificates() {
  const [tab, setTab] = useState("bulk"); // bulk | issue | issued
  const [participants, setParticipants] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [issueModal, setIssueModal] = useState(null);
  const [bulkModal, setBulkModal] = useState(null);
  const [completionDate, setCompletionDate] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [trainingStats, setTrainingStats] = useState({});

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = async () => {
    try {
      const [pRes, cRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/participants`, authHeader()),
        axios.get(`${API}/admin/certificates`, authHeader()),
        axios.get(`${API}/admin/trainings`, authHeader())
      ]);
      // Handle paginated response format
      const participantsData = pRes.data.data || pRes.data;
      const certificatesData = cRes.data.data || cRes.data;
      setParticipants(participantsData);
      setCertificates(certificatesData);
      setTrainings(tRes.data);
      
      // Fetch certificate stats for each training
      const statsPromises = tRes.data.map(t => 
        axios.get(`${API}/admin/trainings/${t.id}/certificate-stats`, authHeader())
          .then(res => ({ id: t.id, ...res.data }))
          .catch(() => ({ id: t.id, total_paid_participants: 0, certificates_issued: 0, pending_certificates: 0 }))
      );
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s; });
      setTrainingStats(statsMap);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Paid participants who don't have a certificate yet
  const issuedParticipantIds = new Set(certificates.map(c => c.participant_id));
  const eligibleParticipants = participants.filter(
    p => p.registration_status === "paid" || p.registration_status === "completed"
  );

  const filteredEligible = eligibleParticipants.filter(p =>
    search === "" ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase()) ||
    p.training_title?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredCerts = certificates.filter(c =>
    search === "" ||
    c.participant_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.participant_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.course_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Trainings with pending certificates
  const trainingsWithPending = trainings.filter(t => {
    const stats = trainingStats[t.id];
    return stats && stats.pending_certificates > 0;
  });

  const handleIssue = async () => {
    if (!completionDate) return;
    setIssuing(true);
    try {
      await axios.post(`${API}/admin/certificates/generate`, {
        participant_id: issueModal.id,
        completion_date: completionDate
      }, authHeader());
      showToast(`Certificate issued for ${issueModal.name}`);
      setIssueModal(null);
      setCompletionDate("");
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to issue certificate", "error");
    } finally { setIssuing(false); }
  };

  const handleBulkIssue = async () => {
    if (!completionDate || !bulkModal) return;
    setIssuing(true);
    try {
      const res = await axios.post(`${API}/admin/trainings/${bulkModal.id}/bulk-issue-certificates`, {
        training_id: bulkModal.id,
        completion_date: completionDate
      }, authHeader());
      showToast(`${res.data.total_issued} certificates issued for ${bulkModal.title}`);
      setBulkModal(null);
      setCompletionDate("");
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to issue certificates", "error");
    } finally { setIssuing(false); }
  };

  const handleSendEmail = async (cert) => {
    setSendingId(cert.id);
    try {
      await axios.post(`${API}/admin/certificates/${cert.id}/send-email`, {}, authHeader());
      showToast(`Certificate sent to ${cert.participant_email}`);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to send email", "error");
    } finally { setSendingId(null); }
  };

  const handleDownload = (cert) => {
    const token = localStorage.getItem("admin_token");
    // Use fetch for auth
    fetch(`${API}/admin/certificates/${cert.id}/download`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate_${cert.certificate_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 shadow-lg border ${
          toast.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"
        }`}>
          {toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          <span className="text-sm font-dm-sans">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-gray-900">Certificates</h1>
          <p className="text-gray-500 text-sm font-dm-sans mt-1">Issue and manage participant certificates</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-5">
        <button
          data-testid="bulk-tab"
          onClick={() => setTab("bulk")}
          className={`px-5 py-3 text-sm font-medium font-dm-sans transition-colors ${
            tab === "bulk" ? "border-b-2 border-black text-black" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Bulk Issue
        </button>
        <button
          data-testid="issue-tab"
          onClick={() => setTab("issue")}
          className={`px-5 py-3 text-sm font-medium font-dm-sans transition-colors ${
            tab === "issue" ? "border-b-2 border-black text-black" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Individual ({eligibleParticipants.length})
        </button>
        <button
          data-testid="issued-tab"
          onClick={() => setTab("issued")}
          className={`px-5 py-3 text-sm font-medium font-dm-sans transition-colors ${
            tab === "issued" ? "border-b-2 border-black text-black" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Issued ({certificates.length})
        </button>
      </div>

      {/* Search - only show for Individual and Issued tabs */}
      {tab !== "bulk" && (
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="cert-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search participants..."
            className="w-full max-w-md border border-gray-200 focus:border-gray-400 pl-9 pr-3 py-2 text-sm font-dm-sans outline-none"
          />
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto" />
        </div>
      ) : tab === "bulk" ? (
        // Bulk Issue Tab - Training-wise certificate issuance
        <div>
          <div className="bg-amber-50 border border-amber-200 p-4 mb-5">
            <p className="text-amber-800 text-sm font-dm-sans">
              <strong>Bulk Issue:</strong> Issue certificates to all paid participants of a training at once. 
              Select a training below to issue certificates.
            </p>
          </div>
          
          {trainings.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 p-16 text-center">
              <Award size={36} className="text-gray-300 mx-auto mb-3" />
              <h3 className="font-outfit font-bold text-gray-700 text-lg mb-1">No trainings found</h3>
              <p className="text-gray-400 text-sm font-dm-sans">Create a training first to issue certificates</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainings.map((training, i) => {
                const stats = trainingStats[training.id] || { total_paid_participants: 0, certificates_issued: 0, pending_certificates: 0 };
                const hasPending = stats.pending_certificates > 0;
                
                return (
                  <motion.div
                    key={training.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`training-cert-card-${training.id}`}
                    className={`bg-white border p-5 transition-all ${
                      hasPending ? 'border-blue-200 hover:border-blue-400' : 'border-gray-200'
                    }`}
                  >
                    <h3 className="font-outfit font-bold text-gray-900 text-base mb-2 line-clamp-2">{training.title}</h3>
                    <p className="text-gray-500 text-xs font-dm-sans mb-4">Instructor: {training.instructor}</p>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-gray-50 p-2 text-center">
                        <div className="text-lg font-bold text-gray-900">{stats.total_paid_participants}</div>
                        <div className="text-[10px] text-gray-500 uppercase">Paid</div>
                      </div>
                      <div className="bg-green-50 p-2 text-center">
                        <div className="text-lg font-bold text-green-700">{stats.certificates_issued}</div>
                        <div className="text-[10px] text-green-600 uppercase">Issued</div>
                      </div>
                      <div className={`p-2 text-center ${hasPending ? 'bg-amber-50' : 'bg-gray-50'}`}>
                        <div className={`text-lg font-bold ${hasPending ? 'text-amber-700' : 'text-gray-400'}`}>
                          {stats.pending_certificates}
                        </div>
                        <div className={`text-[10px] uppercase ${hasPending ? 'text-amber-600' : 'text-gray-400'}`}>Pending</div>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    {hasPending ? (
                      <button
                        data-testid={`bulk-issue-btn-${training.id}`}
                        onClick={() => { setBulkModal(training); setCompletionDate(""); }}
                        className="w-full flex items-center justify-center gap-2 bg-black text-white text-sm font-bold py-2.5 hover:bg-gray-800 transition-colors"
                      >
                        <Award size={14} /> Issue {stats.pending_certificates} Certificates
                      </button>
                    ) : stats.total_paid_participants === 0 ? (
                      <div className="w-full text-center text-gray-400 text-xs font-dm-sans py-2.5 border border-dashed border-gray-200">
                        No paid participants
                      </div>
                    ) : (
                      <div className="w-full text-center text-green-600 text-xs font-bold font-dm-sans py-2.5 bg-green-50 border border-green-200">
                        <CheckCircle size={12} className="inline mr-1" /> All certificates issued
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      ) : tab === "issue" ? (
        filteredEligible.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 p-16 text-center">
            <Award size={36} className="text-gray-300 mx-auto mb-3" />
            <h3 className="font-outfit font-bold text-gray-700 text-lg mb-1">No eligible participants</h3>
            <p className="text-gray-400 text-sm font-dm-sans">Participants who have completed payment will appear here</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 overflow-hidden">
            <table className="w-full text-sm font-dm-sans">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Participant</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Training</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Certificate</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEligible.map((p, i) => {
                  const hasCert = issuedParticipantIds.has(p.id);
                  return (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                      data-testid={`eligible-participant-${p.id}`}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{p.name}</div>
                        <div className="text-gray-400 text-xs">{p.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700 max-w-48 truncate">{p.training_title}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-1 ${
                          p.registration_status === "completed" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                        }`}>
                          {p.registration_status === "completed" ? "Completed" : "Paid"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {hasCert ? (
                          <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1">Issued</span>
                        ) : (
                          <span className="text-xs text-gray-400">Not issued</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!hasCert ? (
                          <button
                            data-testid={`issue-cert-${p.id}`}
                            onClick={() => { setIssueModal(p); setCompletionDate(""); }}
                            className="flex items-center gap-1.5 bg-black text-white text-xs font-bold px-3 py-1.5 hover:bg-gray-800 transition-colors"
                          >
                            <Award size={12} /> Issue
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 font-dm-sans">Already issued</span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        // Issued Certificates Tab
        filteredCerts.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 p-16 text-center">
            <Award size={36} className="text-gray-300 mx-auto mb-3" />
            <h3 className="font-outfit font-bold text-gray-700 text-lg mb-1">No certificates issued yet</h3>
            <p className="text-gray-400 text-sm font-dm-sans">Issue certificates from the "Bulk Issue" or "Individual" tab</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 overflow-hidden">
            <table className="w-full text-sm font-dm-sans">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Participant</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Course</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Cert ID</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCerts.map((c, i) => (
                  <motion.tr
                    key={c.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                    data-testid={`cert-row-${c.id}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{c.participant_name}</div>
                      <div className="text-gray-400 text-xs">{c.participant_email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-40">
                      <div className="truncate">{c.course_name}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{c.completion_date}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-gray-500 font-mono-code">{c.certificate_id}</code>
                    </td>
                    <td className="px-4 py-3">
                      {c.email_sent ? (
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1">Sent</span>
                      ) : (
                        <span className="text-xs text-gray-400">Not sent</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`download-cert-${c.id}`}
                          onClick={() => handleDownload(c)}
                          title="Download PDF"
                          className="p-1.5 border border-gray-200 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                        >
                          <Download size={13} />
                        </button>
                        <button
                          data-testid={`send-cert-${c.id}`}
                          onClick={() => handleSendEmail(c)}
                          disabled={sendingId === c.id}
                          title="Send via email"
                          className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-600 transition-colors text-xs font-bold disabled:opacity-50"
                        >
                          {sendingId === c.id ? (
                            <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send size={12} />
                          )}
                          {sendingId === c.id ? "Sending..." : "Send"}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Issue Certificate Modal (Individual) */}
      <Dialog open={!!issueModal} onOpenChange={() => setIssueModal(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-gray-900">Issue Certificate</DialogTitle>
          </DialogHeader>
          {issueModal && (
            <div className="space-y-4 mt-2">
              <div className="bg-gray-50 border border-gray-200 p-4 space-y-1">
                <div className="text-sm font-medium text-gray-900 font-dm-sans">{issueModal.name}</div>
                <div className="text-xs text-gray-500 font-dm-sans">{issueModal.email}</div>
                <div className="text-xs text-gray-600 font-dm-sans mt-1.5">{issueModal.training_title}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Completion Date *</label>
                <input
                  data-testid="completion-date-input"
                  type="date"
                  value={completionDate}
                  onChange={e => setCompletionDate(e.target.value)}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  required
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 font-dm-sans">
                Certificate will include: Participant name, Course name, Completion date, Instructor name, and a unique Certificate ID.
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="confirm-issue-btn"
                  onClick={handleIssue}
                  disabled={issuing || !completionDate}
                  className="flex-1 bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {issuing ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Issuing...</>
                  ) : (
                    <><Award size={16} /> Issue Certificate</>
                  )}
                </button>
                <button
                  onClick={() => setIssueModal(null)}
                  className="px-5 border border-gray-300 text-gray-700 font-dm-sans text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Issue Certificate Modal */}
      <Dialog open={!!bulkModal} onOpenChange={() => setBulkModal(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-gray-900 flex items-center gap-2">
              <Users size={20} /> Bulk Issue Certificates
            </DialogTitle>
          </DialogHeader>
          {bulkModal && (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 border border-blue-200 p-4 space-y-2">
                <div className="text-sm font-bold text-gray-900 font-outfit">{bulkModal.title}</div>
                <div className="text-xs text-gray-600 font-dm-sans">Instructor: {bulkModal.instructor}</div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1">
                    {trainingStats[bulkModal.id]?.pending_certificates || 0} participants will receive certificates
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">
                  <Calendar size={14} className="inline mr-1" />
                  Completion Date *
                </label>
                <input
                  data-testid="bulk-completion-date-input"
                  type="date"
                  value={completionDate}
                  onChange={e => setCompletionDate(e.target.value)}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1 font-dm-sans">This date will be shown on all certificates</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 font-dm-sans">
                <strong>Note:</strong> This will issue certificates to ALL paid participants who haven't received one yet.
                Participant status will be updated to "Completed".
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="confirm-bulk-issue-btn"
                  onClick={handleBulkIssue}
                  disabled={issuing || !completionDate}
                  className="flex-1 bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {issuing ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Issuing...</>
                  ) : (
                    <><Award size={16} /> Issue All Certificates</>
                  )}
                </button>
                <button
                  onClick={() => setBulkModal(null)}
                  className="px-5 border border-gray-300 text-gray-700 font-dm-sans text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
